import React, { useMemo } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colors } from './Theme/WebTheme';
import { FONT_FAMILY } from '../common/sharedFunctions';
import PeopleIcon from '@mui/icons-material/People';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import StarIcon from '@mui/icons-material/Star';

const StatsSection = React.memo(() => {
  const { i18n } = useTranslation();

  const langCode = useMemo(() => 
    (i18n?.language ? i18n.language.slice(0, 2).toLowerCase() : 'fr'),
    [i18n?.language]
  );

  const statsContent = useMemo(() => ({
    fr: {
      stats: [
        {
          icon: PeopleIcon,
          value: '500+',
          label: 'Utilisateurs actifs',
          sublabel: 'sur notre application mobile'
        },
        {
          icon: ThumbUpIcon,
          value: '100%',
          label: 'Flotte éco-responsable',
          sublabel: 'hybride & électrique'
        },
        {
          icon: DirectionsCarIcon,
          value: '-20%',
          label: 'Tarifs avantageux',
          sublabel: 'vs services traditionnels'
        },
        {
          icon: StarIcon,
          value: '4.9/5',
          label: 'Note application',
          sublabel: 'App Store & Google Play'
        }
      ]
    },
    en: {
      stats: [
        {
          icon: PeopleIcon,
          value: '500+',
          label: 'Active users',
          sublabel: 'on our mobile app'
        },
        {
          icon: ThumbUpIcon,
          value: '100%',
          label: 'Eco-responsible fleet',
          sublabel: 'hybrid & electric'
        },
        {
          icon: DirectionsCarIcon,
          value: '-20%',
          label: 'Competitive rates',
          sublabel: 'vs traditional services'
        },
        {
          icon: StarIcon,
          value: '4.9/5',
          label: 'App rating',
          sublabel: 'App Store & Google Play'
        }
      ]
    }
  }), []);

  const content = useMemo(() => 
    statsContent[langCode] || statsContent.fr,
    [statsContent, langCode]
  );

  return (
    <Box sx={{ 
      py: { xs: 8, md: 12 }, 
      px: { xs: 2, md: 4 },
      backgroundColor: '#0a0a0a',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Animated background */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.05,
        backgroundImage: `linear-gradient(90deg, transparent 0%, ${colors.Button_Primary} 50%, transparent 100%)`,
        animation: 'slide 20s linear infinite',
        '@keyframes slide': {
          '0%': { transform: 'translateX(-100%)' },
          '100%': { transform: 'translateX(100%)' }
        }
      }} />

      <Box sx={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 3, md: 4 }}>
          {content.stats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <Grid item xs={6} md={3} key={index}>
                <Box sx={{
                  textAlign: 'center',
                  p: { xs: 3, md: 4 },
                  borderRadius: '20px',
                  backgroundColor: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(212, 175, 55, 0.15)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: `linear-gradient(90deg, transparent, ${colors.Button_Primary}, transparent)`,
                    transform: 'translateX(-100%)',
                    transition: 'transform 0.6s ease'
                  },
                  '&:hover': {
                    transform: 'translateY(-10px)',
                    backgroundColor: 'rgba(212, 175, 55, 0.08)',
                    borderColor: colors.Button_Primary,
                    boxShadow: `0 16px 48px rgba(212, 175, 55, 0.25)`,
                    '&::before': {
                      transform: 'translateX(100%)'
                    },
                    '& .stat-icon': {
                      transform: 'rotateY(360deg) scale(1.1)',
                      color: colors.Button_Primary
                    }
                  }
                }}>
                  {/* Icon */}
                  <Box 
                    className="stat-icon"
                    sx={{
                      width: { xs: 50, md: 60 },
                      height: { xs: 50, md: 60 },
                      borderRadius: '50%',
                      backgroundColor: 'rgba(212, 175, 55, 0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                      border: `2px solid rgba(212, 175, 55, 0.3)`,
                      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                      transformStyle: 'preserve-3d'
                    }}
                  >
                    <IconComponent sx={{ 
                      fontSize: { xs: 26, md: 32 }, 
                      color: 'rgba(212, 175, 55, 0.8)',
                      transition: 'color 0.3s ease'
                    }} />
                  </Box>

                  {/* Value */}
                  <Typography 
                    sx={{ 
                      fontFamily: FONT_FAMILY,
                      fontSize: { xs: '32px', md: '42px' },
                      fontWeight: 800,
                      color: colors.Button_Primary,
                      mb: 1,
                      lineHeight: 1,
                      textShadow: `0 0 20px rgba(212, 175, 55, 0.3)`
                    }}
                  >
                    {stat.value}
                  </Typography>

                  {/* Label */}
                  <Typography 
                    sx={{ 
                      fontFamily: FONT_FAMILY,
                      fontSize: { xs: '14px', md: '16px' },
                      fontWeight: 700,
                      color: '#fff',
                      mb: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  >
                    {stat.label}
                  </Typography>

                  {/* Sublabel */}
                  <Typography 
                    sx={{ 
                      fontFamily: FONT_FAMILY,
                      fontSize: { xs: '12px', md: '13px' },
                      color: 'rgba(255,255,255,0.6)',
                      lineHeight: 1.4
                    }}
                  >
                    {stat.sublabel}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
});

export default StatsSection;
